import React, { useEffect } from "react";
import { Box, Typography, Link, Button } from "@mui/material";
import styles from "./ToU.module.scss";
import { useAppContext } from "../../context/AppContext";

const ToU = () => {
	const {
		touAccepted,
		setTouAccepted,
		saveTouAcceptance,
		cookiePreferenceSet,
	} = useAppContext();

	// Block scrolling and interaction when ToU is shown
	useEffect(() => {
		if (cookiePreferenceSet && !touAccepted) {
			// Block interaction when ToU needs to be shown
			document.body.style.overflow = 'hidden';
			document.body.style.position = 'fixed';
			document.body.style.top = '0';
			document.body.style.left = '0';
			document.body.style.right = '0';
			document.body.style.pointerEvents = 'none';
		} else {
			// Restore interaction when ToU is accepted or not needed
			document.body.style.overflow = '';
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.left = '';
			document.body.style.right = '';
			document.body.style.pointerEvents = '';
		}

		// Cleanup: ALWAYS restore on unmount
		return () => {
			document.body.style.overflow = '';
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.left = '';
			document.body.style.right = '';
			document.body.style.pointerEvents = '';
		};
	}, [cookiePreferenceSet, touAccepted]);

	const handleAcceptToU = () => {
		setTouAccepted(true);
		saveTouAcceptance(true);
	};

	// Only show if cookies are set but ToU not accepted
	if (!cookiePreferenceSet || touAccepted) {
		return null;
	}

	return (
		<Box className={`${styles.boxContainer} ${styles.blocking}`}>
			<div className={styles.contentContainer}>
				<Typography variant="h4" component="h1" gutterBottom className={styles.title}>
					VIRTUAL CARE ELIGIBILITY PORTAL - TERMS OF USE
				</Typography>
				
				<Typography variant="body2" className={styles.lastUpdated}>
					Last Updated: June, 2025
				</Typography>

				<div className={styles.scrollableContent}>
					<Typography variant="body2" paragraph>
						The following terms of use (the "Terms of Use") govern your use (the term 'use' will mean access or use, as applicable) of the TELUS Health Virtual Care Eligibility Portal (the "Portal"). The Portal provides you with the capability of uploading Virtual Care eligibility lists to the Portal and maintaining the accuracy of the eligibility lists within the Portal (collectively the "Portal Services").
					</Typography>

					<Typography variant="body2" paragraph>
						These Terms of Use form an agreement between TELUS Health Virtual Care Inc. ("THVC") and you. The term "you" refers to the person or corporate entity using the Portal or receiving any Portal Services. A copy of these Terms of Use may be downloaded, saved and printed for your reference. By clicking "I AGREE" or similar electronic acceptance, you agree with these Terms of Use. If you do not accept and agree to be bound by these Terms of Use, please do not use the Portal.
					</Typography>

					<Typography variant="body2" paragraph>
						THVC reserves the right to amend any term of these Terms of Use at any time by giving 30 days' notice. Any notices regarding modifications to these Terms of Use will be in a written form and provided to you: (i) by THVC via email (in each case to the address that you provide); or (ii) via the Portal. For notices made by email, the date of receipt will be deemed the date on which such notice is transmitted. You are not obliged to continue using the Portal Services after an amendment to these Terms of Use is made; however, in the event you choose to not accept the changes, you must immediately cease use of the Portal and the Portal Services and provide written notice to THVC that you are terminating these Terms of Use. Your continued use of the Portal Services following any amendment shall be deemed to be your acceptance of the amended Terms of Use, and waiver of any additional notice requirements.
					</Typography>

					<Typography variant="h6" component="h2" className={styles.sectionHeader}>
						USE OF THE PORTAL
					</Typography>

					<Typography variant="body2" paragraph>
						THVC grants you a personal, revocable, non-exclusive and non-transferable license during the Term to permit you to use the Portal, including for the purpose of accessing the Portal Services in accordance with these Terms of Use.
					</Typography>

					<Typography variant="body2" paragraph>
						As a condition of your use of the Portal, you warrant that: (1) you have reached the age of majority in your jurisdiction of residence; (2) you possess the legal authority to create a binding legal obligation; (3) you will use the Portal in accordance with these Terms of Use; (4) all information supplied by you on the Portal is true, accurate, current and complete; and (5) if you are accessing or using the Portal on behalf of another person or a corporate entity, you represent and warrant that you have the authority to bind such person or entity to these Terms of Use.
					</Typography>

					<Typography variant="h6" component="h2" className={styles.sectionHeader}>
						PORTAL SERVICES
					</Typography>

					<Typography variant="body2" paragraph>
						The Portal Services enable you to upload your organization's Virtual Care eligibility list into a web based application, and to manage and update the eligibility list as needed. The Portal Services are designed to improve the quality of the data provided (eg. removing duplicates, standardizing data formats) making it reliable and ready for use. You remain responsible for the accuracy of the eligibility information that you provide and you acknowledge that inaccurate information provided by you may result in delays for individual users attempting to access Virtual Care services, and/or impacts to your organization's billings for Virtual Care services.
					</Typography>

					<Typography variant="h6" component="h2" className={styles.sectionHeader}>
						PRIVACY
					</Typography>

					<Typography variant="body2" paragraph>
						By providing or uploading any PI (as part of the eligibility lists) to the Portal, you are consenting to the collection, use and disclosure of the PI for the purposes of providing the Portal Services.
					</Typography>

					<Typography variant="body2" paragraph>
						THVC will not disclose PI to any third party except as required to perform the Portal Services, and as may be required by law or as contemplated herein. PI is encrypted in transit and at rest using cryptography, and stored on servers physically located in Canada.
					</Typography>

					<Typography variant="h6" component="h2" className={styles.sectionHeader}>
						USER ACCOUNT
					</Typography>

					<Typography variant="body2" paragraph>
						You (your HR administrator and/or a third party administrator designated by your organization) may be required to successfully sign up for a user account (the "User Account") using the available interfaces within the Portal and you will be issued a username and password login credentials by THVC ("User ID") in order to use the Portal and receive any Portal Services.
					</Typography>

					<Typography variant="h6" component="h2" className={styles.sectionHeader}>
						FEES
					</Typography>

					<Typography variant="body2" paragraph>
						THVC will not charge you any direct Fees for access to and use of the Portal and Portal Services. All Fees are included under your organization's virtual care services agreement.
					</Typography>

					<Typography variant="h6" component="h2" className={styles.sectionHeader}>
						CONTACT
					</Typography>

					<Typography variant="body2" paragraph>
						Should you have any questions regarding these Terms of Use, the Portal or the Portal Services, you may contact THVC Support via email at{" "}
						<Link href="mailto:eligibility@vc.telushealth.com" underline="hover">
							eligibility@vc.telushealth.com
						</Link>
						.
					</Typography>

					<Typography variant="body2" paragraph className={styles.agreementText}>
						By clicking "I AGREE" below, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
					</Typography>
				</div>

				<Box className={styles.buttonWrapper}>
					<Button
						variant="contained"
						className={styles.button}
						onClick={handleAcceptToU}
						size="large"
					>
						I AGREE
					</Button>
				</Box>
			</div>
		</Box>
	);
};

export default ToU;
